import { Link } from "react-router-dom";
import {
  ArrowRight, ScanSmiley, Cpu, Users, Scales,
  Database, ChartLineUp, GridFour, Cube,
  Certificate, Waveform, FunnelSimple, Signature,
} from "@phosphor-icons/react";
import { motion, useReducedMotion } from "framer-motion";
import useSEO from "@/lib/useSEO";
import { Reveal, StaggerGroup, StaggerItem, CountUp } from "@/components/marketing/Motion";
import {
  ContourBackground, PipelineFlow,
} from "@/components/marketing/SresVisuals";
import {
  DrillholeDescent, CommodityOrbit, StandardsMarquee,
} from "@/components/marketing/SresPremiumVisuals";
import ResourceCockpit from "@/components/marketing/ResourceCockpit";
import { easeOutSoft } from "@/lib/motion";

/* ------------------------------------------------------------------ *
 * Section shell                                                       *
 * ------------------------------------------------------------------ */
const Section = ({ children, className = "", testid, id }) => (
  <section id={id} className={`w-full ${className}`} data-testid={testid}>
    <div className="max-w-7xl mx-auto px-6 md:px-10">{children}</div>
  </section>
);

/* ================================================================== *
 * 1 · HERO                                                            *
 *                                                                     *
 * Two-column composition:                                             *
 *   • Left  → identity + copy + CTAs + trust badges                   *
 *   • Right → a live-feel "resource cockpit" panel with a real         *
 *             isometric block model, KPIs, variogram + classification*
 * The whole hero sits on a soft grid + faint contour backdrop.        *
 * ================================================================== */
function Hero() {
  const reduce = useReducedMotion();
  return (
    <section
      className="relative w-full pt-24 md:pt-36 pb-28 md:pb-36 overflow-hidden bg-white"
      data-testid="hero-section"
    >
      {/* Very subtle grid backdrop */}
      <div className="absolute inset-0 grid-lines opacity-40 pointer-events-none" aria-hidden />
      {/* Muted contour accent, top-right */}
      <div className="absolute -right-40 -top-32 w-[680px] h-[600px] opacity-[0.55] pointer-events-none" aria-hidden>
        <ContourBackground />
      </div>
      {/* Hairline separator underneath the hero for editorial rhythm */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-slate-100 pointer-events-none" aria-hidden />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-[1.05fr,1fr] gap-16 lg:gap-24 items-center">
          {/* ───────── Left column ───────── */}
          <div>
            <Reveal variant="small">
              <div className="inline-flex items-center gap-2.5 text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-500">
                <span className="w-6 h-px bg-brand-blue" />
                The Resource Estimation Studio
              </div>
            </Reveal>

            <Reveal variant="hero" delay={0.08}>
              <h1
                className="mt-8 font-heading font-black text-slate-900 text-5xl sm:text-6xl lg:text-[74px] leading-[1.02] tracking-[-0.02em]"
                data-testid="hero-title"
              >
                From drillhole<br />
                to disclosure.
              </h1>
            </Reveal>

            <Reveal delay={0.18}>
              <p className="mt-8 text-slate-600 text-[17px] leading-[1.7] max-w-xl" data-testid="hero-subtitle">
                SRES turns raw drillhole databases into resource statements a Competent
                Person can sign — Ordinary Kriging with fitted variograms, reproducible
                block models, and standards-aligned CPRs. No bolt-ons. No legacy license
                servers. No ambiguity in the audit trail.
              </p>
            </Reveal>

            <Reveal delay={0.28}>
              <div className="mt-11 flex flex-wrap items-center gap-3">
                <Link
                  to="/contact"
                  className="group inline-flex items-center gap-2.5 h-12 px-7 rounded-md bg-slate-900 hover:bg-black text-white text-[13.5px] font-semibold tracking-wide transition-colors"
                  data-testid="hero-cta-primary"
                >
                  Request Demo
                  <ArrowRight size={15} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
                </Link>
                <Link
                  to="/contact"
                  className="group inline-flex items-center gap-2.5 h-12 px-7 rounded-md border border-slate-300 text-slate-800 hover:border-slate-900 hover:text-slate-900 text-[13.5px] font-semibold tracking-wide transition-colors"
                  data-testid="hero-cta-secondary"
                >
                  Talk to a geologist
                  <ArrowRight size={15} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
                </Link>
              </div>
            </Reveal>

            <StaggerGroup
              className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-5 max-w-2xl border-t border-slate-100 pt-8"
              stagger={0.06}
              delay={0.4}
              data-testid="hero-trust-badges"
            >
              <TrustBadge icon={Scales} title="Standards" line="Native by default" />
              <TrustBadge icon={ScanSmiley} title="Reproducible" line="Every single run" />
              <TrustBadge icon={Cpu} title="Kriging" line="At cloud scale" />
              <TrustBadge icon={Users} title="Built with" line="Working CPs" />
            </StaggerGroup>
          </div>

          {/* ───────── Right column — resource cockpit ───────── */}
          <motion.div
            initial={reduce ? false : { opacity: 0, y: 20 }}
            animate={reduce ? false : { opacity: 1, y: 0 }}
            transition={reduce ? undefined : { duration: 0.9, ease: easeOutSoft, delay: 0.15 }}
          >
            <ResourceCockpit />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function TrustBadge({ icon: Icon, title, line }) {
  return (
    <StaggerItem variant="small" className="flex items-start gap-3">
      <Icon size={16} weight="regular" className="text-brand-blue mt-1 shrink-0" />
      <div className="leading-tight">
        <div className="text-[12.5px] font-semibold text-slate-900 tracking-tight">{title}</div>
        <div className="mt-1 text-[11.5px] text-slate-500">{line}</div>
      </div>
    </StaggerItem>
  );
}

/* ------------------------------------------------------------------ *
 * The Resource Cockpit — imported from the shared component.          *
 * ------------------------------------------------------------------ */

/* ================================================================== *
 * 2 · STANDARDS MARQUEE                                               *
 * ================================================================== */
function StandardsStrip() {
  return (
    <section className="w-full bg-white border-y border-slate-100" data-testid="standards-strip">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-[220px,1fr] items-center gap-3 md:gap-8 py-5 md:py-4">
          <div className="text-[10.5px] uppercase tracking-[0.32em] text-slate-400 font-mono md:border-r md:border-slate-100 md:pr-6">
            Standards shipped
          </div>
          <StandardsMarquee />
        </div>
      </div>
    </section>
  );
}

/* ================================================================== *
 * 3 · WORKFLOW · with pipeline flow                                   *
 * ================================================================== */
const WORKFLOW_STEPS = [
  { icon: Database, label: "Ingest",         detail: "Collar · Survey · Assay" },
  { icon: FunnelSimple, label: "QAQC",       detail: "Precision · Bias · HARD" },
  { icon: ChartLineUp, label: "Statistics",  detail: "Log-prob · Top-cut" },
  { icon: Waveform, label: "Variography",    detail: "Fit · Anisotropy" },
  { icon: Cube, label: "Kriging",            detail: "OK · IDW · NN" },
  { icon: GridFour, label: "Classification", detail: "M / I / Inf" },
  { icon: Signature, label: "CPR",           detail: "JORC · NI 43-101" },
];

function WorkflowStrip() {
  return (
    <Section className="pt-28 md:pt-36 pb-24 md:pb-32 bg-white" testid="workflow-strip">
      <div className="max-w-3xl">
        <Reveal variant="small">
          <div className="inline-flex items-center gap-2.5 text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-500">
            <span className="w-6 h-px bg-brand-blue" />
            The SRES Pipeline
          </div>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-6 font-heading font-black text-slate-900 text-3xl md:text-4xl lg:text-[44px] tracking-[-0.02em] leading-[1.08]">
            A single, opinionated pipeline —<br />built for the Competent Person.
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-6 text-slate-600 text-[15.5px] leading-[1.7] max-w-2xl">
            Every stage hands its outputs to the next. Nothing gets lost between tools.
            Every artefact is snapshotted and reproducible bit-for-bit.
          </p>
        </Reveal>
      </div>

      <div className="mt-20 relative">
        <div className="hidden md:block">
          <PipelineFlow nodeCount={WORKFLOW_STEPS.length} />
        </div>

        <StaggerGroup
          className="flex flex-wrap items-start justify-center gap-y-10 relative"
          stagger={0.06}
        >
          {WORKFLOW_STEPS.map((s, i) => (
            <StaggerItem key={s.label} variant="small" className="flex items-center" data-testid={`workflow-node-${i}`}>
              <div className="flex flex-col items-center w-28 md:w-32">
                <div className="w-14 h-14 rounded-xl bg-white border border-slate-200 flex items-center justify-center transition-colors hover:border-slate-900">
                  <s.icon size={22} weight="regular" className="text-slate-700" />
                </div>
                <div className="mt-4 text-[12.5px] text-slate-900 font-semibold text-center leading-tight tracking-tight">
                  {s.label}
                </div>
                <div className="mt-1 text-[10px] text-slate-400 text-center font-mono uppercase tracking-[0.18em] leading-tight">
                  {s.detail}
                </div>
              </div>
              {i < WORKFLOW_STEPS.length - 1 ? (
                <ArrowRight size={14} className="text-slate-300 mx-1 md:mx-2 mt-5" weight="regular" />
              ) : null}
            </StaggerItem>
          ))}
        </StaggerGroup>
      </div>
    </Section>
  );
}

/* ================================================================== *
 * 4 · DRILLHOLE + COMMODITIES · a concept storyboard                  *
 *                                                                     *
 * Left  — drillhole descending through stratigraphy (animation)       *
 * Right — commodity orbit + narrative                                 *
 * ================================================================== */
function ConceptStoryboard() {
  return (
    <Section className="py-24 md:py-32 bg-[#F7F8FB] border-y border-slate-100" testid="concept-storyboard">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-center">
        {/* Left — narrative + drillhole */}
        <div>
          <Reveal variant="small">
            <div className="inline-flex items-center gap-2.5 text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-500">
              <span className="w-6 h-px bg-brand-blue" />
              Multi-commodity
            </div>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-6 font-heading font-black text-slate-900 text-3xl md:text-4xl lg:text-[44px] tracking-[-0.02em] leading-[1.08]">
              From a single drillhole<br />to any commodity you sign off.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-6 text-slate-600 text-[15.5px] leading-[1.7] max-w-lg">
              Fe, Au, Cu, Ag, Ni, U₃O₈, Li, Zn — SRES treats commodity as a
              first-class variable. Price basis, recovery curves, cut-off ranges
              and reporting templates flex to match the resource you&apos;re actually
              estimating.
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <div className="mt-9 flex items-center gap-3">
              <Link
                to="/product"
                className="group inline-flex items-center gap-2 text-slate-900 hover:text-brand-blue font-semibold text-[14px] border-b border-slate-900 hover:border-brand-blue pb-1 transition-colors"
                data-testid="storyboard-cta"
              >
                See every module
                <ArrowRight size={14} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>
          </Reveal>
        </div>

        {/* Right — twin visuals: drillhole + commodity orbit */}
        <Reveal>
          <div className="grid grid-cols-[140px,1fr] gap-10 items-center">
            {/* Drillhole descent — narrow column */}
            <div>
              <div className="text-[10px] uppercase tracking-[0.32em] text-slate-400 font-mono mb-4">
                GVCL-04
              </div>
              <div className="w-full h-[420px]">
                <DrillholeDescent />
              </div>
            </div>
            {/* Commodity orbit */}
            <div>
              <CommodityOrbit />
            </div>
          </div>
        </Reveal>
      </div>
    </Section>
  );
}

/* ================================================================== *
 * 5 · STATS BAR                                                       *
 * ================================================================== */
const STATS = [
  { to: 45,  suffix: " min",  label: "Drillhole → CPR" },
  { to: 5,                    label: "Reporting Standards" },
  { to: 3,   suffix: " sec",  label: "OK · per 100k blocks" },
  { to: 100, suffix: "%",     label: "Reproducible Runs" },
  { to: 0,                    label: "Desktop Installs" },
  { text: "24×7",             label: "Cloud Availability" },
];

function StatsBar() {
  return (
    <section className="w-full bg-[#0A0F1C] text-white py-20 md:py-24 relative overflow-hidden border-y border-black" data-testid="stats-bar">
      {/* Faint grid overlay */}
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage: "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "72px 72px",
        }}
        aria-hidden
      />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <Reveal variant="small">
          <div className="text-[10.5px] uppercase tracking-[0.32em] text-white/50 font-mono mb-10 flex items-center gap-3">
            <span className="w-6 h-px bg-brand-blueLight" />
            The numbers behind the workflow
          </div>
        </Reveal>
        <StaggerGroup className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-y-12 gap-x-6" stagger={0.06}>
          {STATS.map((s, i) => (
            <StaggerItem key={s.label} variant="small" className="text-left" data-testid={`stat-${i}`}>
              <div className="font-heading font-black text-white text-[40px] md:text-[46px] tracking-[-0.02em] leading-none">
                {s.text ? s.text : <CountUp to={s.to} suffix={s.suffix || ""} duration={1.8} />}
              </div>
              <div className="mt-4 text-[10.5px] uppercase tracking-[0.24em] text-white/50 whitespace-nowrap">{s.label}</div>
            </StaggerItem>
          ))}
        </StaggerGroup>
      </div>
    </section>
  );
}

/* ================================================================== *
 * 6 · FEATURES GRID · six studio modules                              *
 * ================================================================== */
const FEATURES = [
  { icon: Database,     title: "Drillhole Compiler",         description: "Collar / Survey / Assay / Geology with control samples · Column alias auto-mapping · Deep validation · Composite length auditing." },
  { icon: Waveform,     title: "Variography",                description: "Experimental variograms · Spherical, exponential and Gaussian model fitting · Anisotropy detection · Per-domain fits." },
  { icon: Cube,         title: "Kriged Block Models",        description: "Ordinary Kriging, Inverse-Distance and Nearest-Neighbour · Composite lengths · Per-lithology domaining and density mapping." },
  { icon: GridFour,     title: "Classification & Confidence",description: "Measured / Indicated / Inferred logic · Search coverage rules · Slope-of-regression · Per-block audit trail." },
  { icon: ChartLineUp,  title: "Economics & Sensitivity",    description: "Floating-cone pit optimisation · Cut-off × NPV sweeps · Monte-Carlo NPV · Price / cost / recovery scenarios." },
  { icon: Certificate,  title: "Competent Person Reports",   description: "JORC / NI 43-101 / SAMREC / CIM / PERC-aligned CPR PDFs · Signature blocks · Watermarked, expiring share links." },
];

function FeaturesGrid() {
  return (
    <Section className="py-28 md:py-36 bg-white" testid="features-grid-section">
      <div className="max-w-3xl">
        <Reveal variant="small">
          <div className="inline-flex items-center gap-2.5 text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-500">
            <span className="w-6 h-px bg-brand-blue" />
            Six Studio Modules
          </div>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-6 font-heading font-black text-slate-900 text-3xl md:text-4xl lg:text-[44px] tracking-[-0.02em] leading-[1.08]">
            Everything the Competent Person needs.<br />Nothing they don&apos;t.
          </h2>
        </Reveal>
      </div>
      <StaggerGroup className="mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-100 border border-slate-100 rounded-2xl overflow-hidden" stagger={0.05}>
        {FEATURES.map((f, i) => (
          <StaggerItem
            key={f.title}
            className="p-9 bg-white hover:bg-slate-50 transition-colors duration-300 group relative"
            data-testid={`feature-card-${i}`}
          >
            <div className="text-[10.5px] font-mono uppercase tracking-[0.28em] text-slate-400">{String(i + 1).padStart(2, "0")}</div>
            <div className="mt-5 w-10 h-10 flex items-center justify-center border border-slate-200 rounded-lg group-hover:border-slate-900 transition-colors">
              <f.icon size={20} weight="regular" className="text-slate-700" />
            </div>
            <div className="mt-6 font-heading font-bold text-slate-900 text-[19px] tracking-tight">{f.title}</div>
            <p className="mt-3 text-slate-600 text-[14.5px] leading-[1.7]">{f.description}</p>
          </StaggerItem>
        ))}
      </StaggerGroup>
    </Section>
  );
}

/* ================================================================== *
 * 7 · WHY SRES                                                        *
 * ================================================================== */
const WHY = [
  { title: "Estimation-first, not lifecycle-broad",     body: "SRES is a scalpel — not a Swiss-army knife. Every screen is dedicated to the specialist work of turning drill assays into a signed resource statement." },
  { title: "Reproducibility is a first-class citizen",  body: "Every kriging run, pit optimisation and CPR is snapshotted with its inputs. Anything you signed a year ago can be re-generated bit-for-bit today." },
  { title: "Standards native — not standards-adjacent", body: "JORC, NI 43-101, SAMREC, CIM and PERC templates ship out of the box. Table 1 disclosures are captured inline with the workflow, not bolted on at the end." },
  { title: "Written with the CPs who sign",             body: "Our product roadmap is co-owned by Competent Persons who put their name on real resource statements — quarterly, publicly, personally accountable." },
];

function WhySRES() {
  return (
    <Section className="py-28 md:py-36 bg-[#F7F8FB]" testid="why-sres-section">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr,1.3fr] gap-16">
        <div>
          <Reveal variant="small">
            <div className="inline-flex items-center gap-2.5 text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-500">
              <span className="w-6 h-px bg-brand-blue" />
              Why SRES
            </div>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-6 font-heading font-black text-slate-900 text-3xl md:text-4xl lg:text-[44px] tracking-[-0.02em] leading-[1.08]">
              A studio, not a suite.<br />Depth over breadth.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-6 text-slate-600 text-[15.5px] leading-[1.7] max-w-lg">
              Where broad mining suites offer a hundred half-answers, SRES delivers the
              six modules the Competent Person actually signs off — done with the
              geostatistical rigour that stands up to an audit.
            </p>
          </Reveal>
        </div>
        <StaggerGroup className="grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-200/60 border border-slate-200/60 rounded-2xl overflow-hidden bg-white" stagger={0.05}>
          {WHY.map((w, i) => (
            <StaggerItem
              key={w.title}
              className="p-8 bg-white hover:bg-slate-50 transition-colors duration-300"
              data-testid={`why-card-${i}`}
            >
              <div className="text-[10.5px] font-mono uppercase tracking-[0.28em] text-slate-400">{String(i + 1).padStart(2, "0")}</div>
              <div className="mt-4 font-heading font-bold text-slate-900 text-[17px] tracking-tight leading-snug">{w.title}</div>
              <p className="mt-3 text-slate-600 text-[14px] leading-[1.7]">{w.body}</p>
            </StaggerItem>
          ))}
        </StaggerGroup>
      </div>
    </Section>
  );
}

/* ================================================================== *
 * 8 · TECH STRIP                                                      *
 * ================================================================== */
const TECH = [
  "PyKrige", "GeoStatsPy", "GSLIB conventions", "SciPy · variogram fit",
  "NumPy · block ops", "Pandas · assay tables", "Shapely", "GeoPandas",
  "Plotly WebGL", "ReportLab · CPR", "FastAPI", "MongoDB Atlas",
];

function TechStrip() {
  return (
    <section className="w-full bg-white py-24 md:py-28 border-y border-slate-100" data-testid="technology-strip">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl">
          <Reveal variant="small">
            <div className="inline-flex items-center gap-2.5 text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-500">
              <span className="w-6 h-px bg-brand-blue" />
              Geoscience stack
            </div>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-6 font-heading font-black text-slate-900 text-2xl md:text-3xl lg:text-[36px] tracking-[-0.02em] leading-[1.15]">
              The libraries a resource geologist would choose —<br />wired together properly.
            </h2>
          </Reveal>
        </div>
        <StaggerGroup className="mt-12 flex flex-wrap items-center gap-2.5" stagger={0.03}>
          {TECH.map((t) => (
            <StaggerItem
              key={t}
              variant="small"
              as="span"
              className="inline-flex items-center h-9 px-4 rounded-md bg-slate-50 border border-slate-200 text-slate-700 text-[12.5px] font-medium tracking-tight hover:border-slate-900 hover:text-slate-900 transition-colors"
              data-testid={`tech-pill-${t.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
            >
              {t}
            </StaggerItem>
          ))}
        </StaggerGroup>
      </div>
    </section>
  );
}

/* ================================================================== *
 * 9 · CTA BANNER                                                      *
 * ================================================================== */
function CTABanner() {
  return (
    <Section className="py-24 md:py-32 bg-white" testid="cta-banner-section">
      <Reveal>
        <div
          className="relative overflow-hidden rounded-2xl bg-[#0A0F1C] text-white px-8 md:px-14 py-14 md:py-16 flex flex-col lg:flex-row items-start lg:items-center gap-10 lg:gap-16 border border-slate-900"
          data-testid="cta-banner"
        >
          {/* Fine grid overlay for depth */}
          <div
            className="absolute inset-0 opacity-[0.05] pointer-events-none"
            style={{
              backgroundImage: "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
              backgroundSize: "60px 60px",
            }}
            aria-hidden
          />
          {/* Left accent stripe */}
          <div className="absolute left-0 top-8 bottom-8 w-px bg-brand-blue/60" aria-hidden />

          <div className="flex-1 relative">
            <div className="inline-flex items-center gap-2.5 text-[10.5px] font-semibold uppercase tracking-[0.32em] text-white/50">
              <span className="w-6 h-px bg-brand-blueLight" />
              Book a working session
            </div>
            <h3 className="mt-6 font-heading font-black text-white text-3xl md:text-[40px] leading-[1.1] tracking-[-0.02em] max-w-2xl">
              Ready to defend your next resource statement?
            </h3>
            <p className="mt-5 text-white/70 text-[15px] leading-[1.7] max-w-2xl">
              Bring your drillhole database. In a single 45-minute session we&apos;ll krig
              your first block model, generate a draft CPR PDF, and walk you through
              every assumption behind it — with the auditor already in mind.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row lg:flex-col gap-3 shrink-0 relative">
            <Link
              to="/contact"
              className="group inline-flex items-center justify-between gap-3 h-12 px-6 rounded-md bg-white text-slate-900 text-[13.5px] font-semibold hover:bg-slate-100 transition-colors min-w-[220px]"
              data-testid="cta-banner-primary"
            >
              Request Demo
              <ArrowRight size={14} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/contact"
              className="group inline-flex items-center justify-between gap-3 h-12 px-6 rounded-md border border-white/25 text-white text-[13.5px] font-semibold hover:border-white transition-colors min-w-[220px]"
              data-testid="cta-banner-secondary"
            >
              Talk to a CP
              <ArrowRight size={14} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>
      </Reveal>
    </Section>
  );
}

/* ================================================================== *
 * PAGE                                                                *
 * ================================================================== */
export default function LandingPage() {
  useSEO({
    title: "The Resource Estimation Studio",
    description: "SRES is a specialist studio for defensible resource estimation — drillhole ingest, QAQC, variography, Ordinary Kriging, classification and Competent Person Reports aligned to JORC, NI 43-101 and SAMREC.",
    keywords: "resource estimation, kriging, block model, JORC, NI 43-101, SAMREC, competent person report, drillhole database, mining software",
    path: "/",
  });
  return (
    <div data-testid="landing-page">
      <Hero />
      <StandardsStrip />
      <WorkflowStrip />
      <ConceptStoryboard />
      <StatsBar />
      <FeaturesGrid />
      <WhySRES />
      <TechStrip />
      <CTABanner />
    </div>
  );
}
